/**
 * 租户登录页面
 * 纯租户端系统，所有用户统一跳转到租户工作台
 * 支持密码登录和扫码登录两种方式
 *
 * REQ-20260508-0001 #10179: 增强项
 *   - 失败 3 次后强制图形验证码（AC12）
 *   - 平台超管隐藏入口（footer 链接 → /platform/login）
 *   - 租户主题动态加载（AC8: 基于子域 code 调 /auth/tenant-theme）
 *   - LoginOutcome 双形态处理（已在 #10178 接入）
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Form,
  Input,
  Button,
  Checkbox,
  Alert,
  message,
  notification,
  Tabs,
  QRCode,
  Spin,
  Typography,
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  CloudServerOutlined,
  GlobalOutlined,
  QrcodeOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  MobileOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, quickLoginApi, qrcodeGenerate, qrcodeCheck, type LoginParams } from '@/modules/auth';
// REQ-20260508-0001 #10179: 公开租户主题查询（AC8）
import { getPublicTenantThemeByCode } from '@/modules/auth/api/auth';
import { getTenantByDomain } from '@/api/modules/platform';
import { ossProxyUrl } from '@/utils/ossUpload';
import { applyTenantFavicon, resolveTenantFaviconUrl } from '@/utils/favicon';
import { getRedirectPath as computeRedirectPath, type RedirectUserContext } from '@/modules/auth/utils/redirect';
import { exitAdminMode } from '@/modules/foundation/api/adminMode';
import type { QRCodeCheckResult, TenantThemeResult } from '@/modules/auth/api/types';
import { STORAGE_KEYS, setStorageItem, getStorageItem } from '@/utils/storage';
import { supportedLanguages } from '@/locales';
import i18n from '@/locales';
import { getMyOrganizations, switchOrganization, type MyOrganization } from '@/api/organization';
import OrganizationSelectModal from '@/components/OrganizationSelectModal';
import './index.less';

const { Text } = Typography;

interface LoginForm {
  username: string;
  password: string;
  captcha: string;
  remember: boolean;
}

const POLL_INTERVAL = 2000; // 轮询间隔 2 秒

// REQ-20260508-0001 #10179 AC12: 连续失败 3 次后强制图形验证码
// (CR fix #6): 改用 localStorage 跨 tab 共享，避免多 tab 绕过阈值
//   - 后端 Redis IP 限流是真正防御层；前端是 UX 辅助
//   - localStorage 在用户清缓存后会重置（可接受）
const CAPTCHA_THRESHOLD = 3;
const FAIL_COUNT_KEY = 'login_fail_count';

/** 读失败计数（localStorage 跨 tab 共享） */
function getFailCount(): number {
  const raw = localStorage.getItem(FAIL_COUNT_KEY);
  return raw ? parseInt(raw, 10) || 0 : 0;
}
/** 后端按账号累计失败次数，已要求验证码时直接把本地计数拉到阈值（换浏览器或清缓存后也能弹出验证码） */
const CAPTCHA_REQUIRED_MESSAGE = '请输入验证码';
function markFailure(reason?: string): number {
  const next = reason === CAPTCHA_REQUIRED_MESSAGE
    ? Math.max(getFailCount() + 1, CAPTCHA_THRESHOLD)
    : getFailCount() + 1;
  localStorage.setItem(FAIL_COUNT_KEY, String(next));
  return next;
}
/** 清零失败计数（成功登录） */
function clearFailCount() {
  localStorage.removeItem(FAIL_COUNT_KEY);
}

/**
 * REQ-20260508-0001 #10179 AC8: 从浏览器 hostname 提取租户子域
 * jia.coolbuy.com → "jia"
 * paas.coolbuy.com / localhost → null（不加载租户主题）
 */
function extractTenantCode(hostname: string): string | null {
  if (hostname === 'localhost' || hostname.startsWith('127.') || hostname.startsWith('192.168.')) {
    return null;
  }
  const parts = hostname.split('.');
  if (parts.length < 3) return null;
  const sub = parts[0];
  if (sub === 'paas' || sub === 'www' || sub === 'admin' || sub === 'mall' || sub === 'platform') {
    return null;
  }
  return sub;
}

const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // 状态管理
  const { login: authLogin, getCaptcha: fetchCaptcha } = useAuth();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [quickLoginLoading, setQuickLoginLoading] = useState(false);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [errorAlert, setErrorAlert] = useState(false);
  const [captchaData, setCaptchaData] = useState({
    captchaData: '',
    captchaId: ''
  });

  // QR Code 状态
  const [activeTab, setActiveTab] = useState<string>('password');
  const [qrcodeUrl, setQrcodeUrl] = useState('');
  const [qrcodeStatus, setQrcodeStatus] = useState<string>('idle'); // idle | pending | scanned | expired | error
  const [qrcodeLoading, setQrcodeLoading] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentQrcodeIdRef = useRef<string>('');

  // 组织选择弹窗状态
  const [showOrgModal, setShowOrgModal] = useState(false);

  // REQ-20260508-0001 #10179 AC12: 失败计数 → 决定是否需要图形验证码
  const [failCount, setFailCount] = useState<number>(getFailCount());
  const captchaRequired = failCount >= CAPTCHA_THRESHOLD;

  // REQ-20260508-0001 #10179 AC8: 租户主题（基于子域加载）
  const [tenantTheme, setTenantTheme] = useState<TenantThemeResult | null>(null);

  // 访问域名在平台绑定了租户：品牌整套换成该租户，登录只认该租户体系的账号
  const [domainTenant, setDomainTenant] = useState(false);

  // 加载租户主题：先按访问域名找绑定的租户；没绑定再按子域 code（REQ-20260617-0003 P2）
  useEffect(() => {
    const host = window.location.hostname;
    const loadByCode = () => {
      const code = extractTenantCode(host);
      if (!code) return;
      getPublicTenantThemeByCode(code)
        .then((res) => {
          const data = res.data;
          if (data && data.code) {
            setTenantTheme(data);
            // 应用主色到 CSS 变量（轻量集成，不破坏现有 LESS 主题）
            if (data.primaryColor) {
              document.documentElement.style.setProperty('--tenant-primary', data.primaryColor);
            }
          }
        })
        .catch((err) => {
          // REQ-20260510-0008 [CR-Low-L5]：始终输出 warn 便于线上排障
          // UI 仍降级到默认 Coolbuy 主题（不影响功能），仅日志告知子域主题加载失败
          console.warn('[tenant-theme] 加载失败，降级到默认主题:', code, err);
        });
    };
    getTenantByDomain(host)
      .then((res) => {
        const d = res.data;
        if (!d?.matched) {
          loadByCode();
          return;
        }
        setDomainTenant(true);
        setTenantTheme({
          code: d.tenantCode || '',
          displayName: d.tenantName,
          logoUrl: d.logoUrl ? ossProxyUrl(d.logoUrl) : undefined,
          primaryColor: d.primaryColor,
          subtitle: d.subtitle,
        });
        if (d.primaryColor) {
          document.documentElement.style.setProperty('--tenant-primary', d.primaryColor);
        }
        if (d.tenantName) document.title = d.tenantName;
        const favicon = resolveTenantFaviconUrl(d.faviconUrl, d.logoUrl);
        if (favicon) applyTenantFavicon(ossProxyUrl(favicon));
      })
      .catch((err) => {
        console.warn('[tenant-theme] 自定义域名解析失败，按子域加载:', host, err);
        loadByCode();
      });
  }, []);

  // 检查是否启用快速登录（仅开发环境）
  const enableQuickLogin = import.meta.env.VITE_ENABLE_QUICK_LOGIN === 'true';

  // 获取验证码
  const handleGetCaptcha = async () => {
    setCaptchaLoading(true);
    try {
      const response = await fetchCaptcha();
      const captchaResult = response.data || response;
      setCaptchaData({
        captchaData: captchaResult.captchaData,
        captchaId: captchaResult.captchaId,
      });
      setErrorAlert(false);
    } catch (error) {
      console.error('获取验证码失败:', error);
      message.error(t('login.captchaError', '获取验证码失败'));
    } finally {
      setCaptchaLoading(false);
    }
  };

  // 获取跳转路径 - REQ-20260508-0001: 基于 available_portals 智能分流
  // REQ-20260510-0008 [CR-Low-L7]：纯函数移到 utils/redirect.ts，便于单测
  // 此处仅做 localStorage 适配（读取 USER_INFO 后委托纯函数）
  const getRedirectPath = (): string => {
    const userInfo = getStorageItem<RedirectUserContext>(STORAGE_KEYS.USER_INFO);
    return computeRedirectPath(userInfo);
  };

  // 检查并处理用户组织
  const checkAndSetupOrganization = async (): Promise<boolean> => {
    try {
      const result = await getMyOrganizations();

      if (!result || !result.items || result.items.length === 0) {
        return true;
      }

      const currentOrg = result.items.find((org) => (org as MyOrganization & { isCurrent?: boolean }).isCurrent ?? org.is_current);
      if (currentOrg) {
        return true;
      }

      if (result.items.length === 1) {
        try {
          const switchResult = await switchOrganization(result.items[0].id);
          if (switchResult && switchResult.token) {
            setStorageItem(STORAGE_KEYS.TOKEN, switchResult.token);
          }
        } catch (err) {
          console.warn('自动设置组织失败，继续登录流程');
        }
        return true;
      }

      setShowOrgModal(true);
      return false;
    } catch (error) {
      console.error('检查组织失败:', error);
      return true;
    }
  };

  // 组织选择成功回调
  const handleOrgSelectSuccess = () => {
    setShowOrgModal(false);
    const redirectPath = getRedirectPath();
    setTimeout(() => {
      window.location.href = redirectPath;
    }, 500);
  };

  // ==================== QR Code 登录 ====================

  // 停止轮询
  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // 处理扫码登录成功
  const handleQRCodeLoginSuccess = useCallback(async (data: QRCodeCheckResult) => {
    stopPolling();
    setQrcodeStatus('confirmed');

    const { accessToken, userInfo: userInfoData, refreshToken } = data;

    if (!accessToken || !userInfoData) {
      message.error('扫码登录响应数据异常');
      return;
    }

    // 保存 token
    localStorage.setItem(STORAGE_KEYS.TOKEN, accessToken);
    if (refreshToken) {
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    }

    // 保存用户信息
    const rawRoles = userInfoData.roles || [];
    const roleCodes: string[] = rawRoles.map((r: string | { roleCode?: string; code?: string }) =>
      typeof r === 'string' ? r : (r.roleCode || r.code || '')
    ).filter(Boolean);

    const userInfo = {
      id: userInfoData.userId,
      username: userInfoData.username,
      tenant_id: userInfoData.tenantId,
      tenant_code: userInfoData.tenantCode,
      tenant_name: userInfoData.tenantName,
      real_name: userInfoData.realName,
      email: userInfoData.email,
      phone: userInfoData.phone,
      avatar: userInfoData.avatar,
      user_scope: userInfoData.userScope || 'tenant',
      roles: rawRoles,
      role_codes: roleCodes,
      permissions: userInfoData.permissions || [],
      // 分销层级：0=顶级租户（品牌商），1+=下级客户
      distribution_level: userInfoData.distributionLevel,
    };
    setStorageItem(STORAGE_KEYS.USER_INFO, userInfo);

    // 菜单不在此处预取：进入租户端后由 TenantLayout/useMenuItems 按 work_mode 拉取

    notification.success({
      message: t('login.success', '登录成功'),
      description: t('login.welcomeBack', '欢迎回来！'),
      duration: 3,
    });

    // 检查组织
    const canProceed = await checkAndSetupOrganization();
    if (canProceed) {
      const redirectPath = getRedirectPath();
      setTimeout(() => {
        window.location.href = redirectPath;
      }, 800);
    }
  }, [stopPolling, t]);

  // 开始轮询 QR Code 状态
  const startPolling = useCallback((id: string) => {
    stopPolling();
    pollTimerRef.current = setInterval(async () => {
      try {
        const response = await qrcodeCheck(id);
        const result = response.data || response;
        const status = result.status;

        if (status === 'scanned') {
          setQrcodeStatus('scanned');
        } else if (status === 'confirmed') {
          handleQRCodeLoginSuccess(result as QRCodeCheckResult);
        } else if (status === 'expired' || status === 'canceled') {
          stopPolling();
          setQrcodeStatus('expired');
        }
      } catch (error) {
        // 网络错误不停止轮询
        console.warn('QR code poll error:', error);
      }
    }, POLL_INTERVAL);
  }, [stopPolling, handleQRCodeLoginSuccess]);

  // 生成二维码
  const generateQRCode = useCallback(async () => {
    stopPolling();
    setQrcodeLoading(true);
    setQrcodeStatus('idle');

    try {
      const response = await qrcodeGenerate();
      const result = response.data || response;
      currentQrcodeIdRef.current = result.qrcodeId;
      setQrcodeUrl(result.qrcodeUrl);
      setQrcodeStatus('pending');
      startPolling(result.qrcodeId);
    } catch (error) {
      console.error('生成二维码失败:', error);
      setQrcodeStatus('error');
      message.error('生成二维码失败，请重试');
    } finally {
      setQrcodeLoading(false);
    }
  }, [stopPolling, startPolling]);

  // Tab 切换时
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    if (key === 'qrcode') {
      generateQRCode();
    } else {
      stopPolling();
    }
  };

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  // ==================== 密码登录 ====================

  // 登录提交
  const handleSubmit = async (values: LoginForm) => {
    // REQ-20260508-0001 #10179 AC12: 仅当 captchaRequired 时校验验证码
    if (captchaRequired) {
      if (!captchaData.captchaId) {
        message.error(t('login.captchaRequired', '请先获取验证码'));
        return;
      }
      if (!values.captcha || values.captcha.length !== 4) {
        message.error(t('login.captchaInvalid', '请输入正确的验证码'));
        return;
      }
    }
    setSubmitLoading(true);
    try {
      const params: LoginParams = {
        username: values.username,
        password: values.password,
        captcha: values.captcha || '',
        captcha_id: captchaData.captchaId || '',
        remember: values.remember,
        host: window.location.hostname,
      };
      // REQ-20260508-0001: login() 返回 LoginOutcome（kind: success/tenant-select/failure）
      const outcome = await authLogin(params);
      if (outcome.kind === 'tenant-select') {
        clearFailCount();
        navigate('/tenant-select', { replace: true });
        return;
      }
      if (outcome.kind === 'success') {
        // REQ-20260508-0001 AC12: 成功时清失败计数
        clearFailCount();
        setFailCount(0);
        setErrorAlert(false);
        notification.success({
          message: t('login.success', '登录成功'),
          description: t('login.welcomeBack', '欢迎回来！'),
          duration: 3,
        });

        const canProceed = await checkAndSetupOrganization();
        if (canProceed) {
          const redirectPath = getRedirectPath();
          // 登录时清除上一次的 admin mode 会话，避免跳转后界面停留在管理模式
          exitAdminMode('login').catch(() => {/* 忽略，不阻塞跳转 */});
          // REQ-20260508-0001: /mall 是另一前端项目，需整页跳转
          if (redirectPath === '/mall' || redirectPath.startsWith('/mall/')) {
            window.location.href = redirectPath;
          } else {
            navigate(redirectPath, { replace: true });
          }
        }
      } else {
        // failure - REQ-20260508-0001 AC12: 失败计数 +1
        const next = markFailure(outcome.reason);
        setFailCount(next);
        // 达阈值后获取验证码（之后弹出输入框）
        if (next >= CAPTCHA_THRESHOLD) {
          await handleGetCaptcha();
        }
        form.setFieldValue('captcha', '');
        message.error(outcome.reason || t('login.failed', '登录失败'));
        setErrorAlert(true);
      }
    } catch (error) {
      const next = markFailure((error as Error).message);
      setFailCount(next);
      setErrorAlert(true);
      form.setFieldValue('captcha', '');
      if (next >= CAPTCHA_THRESHOLD) {
        await handleGetCaptcha();
      }
      message.error((error as Error).message || t('login.failed', '登录失败'));
    } finally {
      setSubmitLoading(false);
    }
  };

  // 快速登录（仅开发环境）
  const handleQuickLogin = async (username: string = 'lining_admin') => {
    setQuickLoginLoading(true);
    try {
      const response = await quickLoginApi(username);
      const responseData = response.data;

      if (!responseData) {
        throw new Error('快速登录响应数据为空');
      }

      const { accessToken, userInfo: userInfoData } = responseData;

      if (accessToken && userInfoData) {
        localStorage.setItem(STORAGE_KEYS.TOKEN, accessToken);

        const userInfo = {
          id: userInfoData.userId,
          username: userInfoData.username,
          tenant_id: userInfoData.tenantId,
          tenant_name: userInfoData.tenantName,
          tenant_code: userInfoData.tenantCode,
          real_name: userInfoData.realName,
          user_scope: userInfoData.userScope,
          avatar: userInfoData.avatar,
          roles: userInfoData.roles?.map((r: { roleId: number; roleName: string; roleCode: string }) => ({
            id: r.roleId,
            name: r.roleName,
            code: r.roleCode,
          })) || [],
          // 分销层级：0=顶级租户（品牌商），1+=下级客户
          distribution_level: userInfoData.distributionLevel,
        };
        localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(userInfo));

        // 菜单不在此处预取：进入租户端后由 TenantLayout/useMenuItems 按 work_mode 拉取

        setErrorAlert(false);
        notification.success({
          message: t('login.quickLoginSuccess', '快速登录成功'),
          description: `${t('login.welcomeBack', '欢迎回来')}，${userInfo.tenant_name || userInfo.username}！`,
          duration: 1.5,
        });

        const canProceed = await checkAndSetupOrganization();
        if (canProceed) {
          const redirectPath = getRedirectPath();
          setTimeout(() => {
            window.location.href = redirectPath;
          }, 800);
        }
      }
    } catch (error) {
      console.error('快速登录失败:', error);
      message.error((error as Error).message || t('login.quickLoginFailed', '快速登录失败'));
      setErrorAlert(true);
    } finally {
      setQuickLoginLoading(false);
    }
  };

  // 页面初始化（REQ-20260508-0001 AC12: 仅当历史失败次数已达阈值时预加载验证码）
  useEffect(() => {
    if (captchaRequired) {
      handleGetCaptcha();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==================== 渲染 QR Code 内容 ====================

  const renderQRCodeContent = () => {
    if (qrcodeLoading) {
      return (
        <div className="qr-login-container">
          <div className="qr-code-wrapper">
            <Spin size="large" />
          </div>
          <Text type="secondary">正在生成二维码...</Text>
        </div>
      );
    }

    if (qrcodeStatus === 'error' || qrcodeStatus === 'idle') {
      return (
        <div className="qr-login-container">
          <div className="qr-code-wrapper qr-code-placeholder">
            <QrcodeOutlined style={{ fontSize: 80, color: '#d9d9d9' }} />
          </div>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={generateQRCode}
          >
            生成二维码
          </Button>
        </div>
      );
    }

    if (qrcodeStatus === 'expired') {
      return (
        <div className="qr-login-container">
          <div className="qr-code-wrapper qr-code-expired">
            <QRCode value={qrcodeUrl || 'expired'} size={200} status="expired" />
          </div>
          <Text type="secondary">二维码已过期</Text>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={generateQRCode}
            style={{ marginTop: 8 }}
          >
            刷新二维码
          </Button>
        </div>
      );
    }

    if (qrcodeStatus === 'scanned') {
      return (
        <div className="qr-login-container">
          <div className="qr-code-wrapper qr-code-scanned">
            <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a' }} />
          </div>
          <Text strong style={{ fontSize: 16 }}>扫码成功</Text>
          <Text type="secondary">请在手机上确认登录</Text>
        </div>
      );
    }

    if (qrcodeStatus === 'confirmed') {
      return (
        <div className="qr-login-container">
          <div className="qr-code-wrapper qr-code-scanned">
            <Spin size="large" />
          </div>
          <Text strong style={{ fontSize: 16 }}>登录中...</Text>
        </div>
      );
    }

    // pending - 正常显示二维码
    return (
      <div className="qr-login-container">
        <div className="qr-code-wrapper">
          <QRCode value={qrcodeUrl} size={200} />
        </div>
        <div className="qr-hint">
          <MobileOutlined style={{ marginRight: 4 }} />
          <Text type="secondary">请使用 APP 扫描二维码登录</Text>
        </div>
        <Button
          type="link"
          size="small"
          icon={<ReloadOutlined />}
          onClick={generateQRCode}
        >
          刷新
        </Button>
      </div>
    );
  };

  // ==================== 页面渲染 ====================

  return (
    <div className="tenant-login-container">
      {/* 斜切背景 */}
      <div className="bg-slash" />
      <div className="bg-stripe bg-stripe-1" />
      <div className="bg-stripe bg-stripe-2" />
      <div className="bg-stripe bg-stripe-3" />
      {!domainTenant && <div className="bg-text">COOLBUY</div>}

      <div className="tenant-login-content">
        <div className="login-card">
          {/* 头部 — 横向排布 */}
          <div className="login-header">
            <div className="header-row">
              <div className="logo-area">
                {/* REQ-20260508-0001 AC8: 优先用租户 logo，无则用默认 icon */}
                {tenantTheme?.logoUrl ? (
                  <img
                    src={tenantTheme.logoUrl}
                    alt={tenantTheme.displayName || ''}
                    style={{ height: 40, maxWidth: 160, objectFit: 'contain' }}
                  />
                ) : (
                  <CloudServerOutlined className="default-icon" />
                )}
              </div>
              <h1 className="login-title">
                {tenantTheme?.displayName || t('login.title', '酷采系统')}
              </h1>
            </div>
            <p className="login-subtitle">
              {tenantTheme?.subtitle || (domainTenant ? '' : t('login.subtitle', 'Coolbuy PaaS System'))}
            </p>
          </div>

          {/* 登录方式 Tabs */}
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            centered
            items={[
              {
                key: 'password',
                label: (
                  <span><UserOutlined /> {t('login.passwordTab', '密码登录')}</span>
                ),
                children: (
                  <>
                    <Form
                      form={form}
                      name="login"
                      onFinish={handleSubmit}
                      autoComplete="off"
                      size="large"
                      layout="vertical"
                      initialValues={{ remember: true }}
                    >
                      {errorAlert && (
                        <Alert
                          message={t('login.errorMessage', '用户名或密码错误，请重试')}
                          type="error"
                          showIcon
                          style={{ marginBottom: 24 }}
                        />
                      )}

                      <Form.Item
                        name="username"
                        label={t('login.usernameLabel', '用户名')}
                        rules={[{ required: true, message: t('login.usernameRequired', '请输入用户名') }]}
                      >
                        <Input
                          prefix={<UserOutlined />}
                          placeholder={t('login.usernamePlaceholder', '请输入用户名')}
                          allowClear
                          autoComplete="username"
                        />
                      </Form.Item>

                      <Form.Item
                        name="password"
                        label={t('login.passwordLabel', '密码')}
                        rules={[{ required: true, message: t('login.passwordRequired', '请输入密码') }]}
                      >
                        <Input.Password
                          prefix={<LockOutlined />}
                          placeholder={t('login.passwordPlaceholder', '请输入密码')}
                          allowClear
                          autoComplete="current-password"
                        />
                      </Form.Item>

                      {/* REQ-20260508-0001 #10179 AC12: 仅失败 N 次后渲染验证码 */}
                      {captchaRequired && (
                        <Form.Item
                          name="captcha"
                          label={
                            <span>
                              {t('login.captchaLabel', '验证码')}
                              <span style={{ color: '#f43f5e', marginLeft: 4 }}>*</span>
                            </span>
                          }
                          rules={[{ required: true, message: t('login.captchaRequired', '请输入验证码') }]}
                          extra={
                            <span style={{ fontSize: 11, color: '#94a3b8' }}>
                              连续失败 {failCount} 次，请输入图形验证码
                            </span>
                          }
                        >
                          <div className="captcha-container">
                            <Input
                              prefix={<SafetyCertificateOutlined />}
                              placeholder={t('login.captchaPlaceholder', '请输入验证码')}
                              allowClear
                              autoComplete="off"
                              maxLength={4}
                              style={{ flex: 1, marginRight: 8 }}
                            />
                            <Button
                              className="captcha-button"
                              loading={captchaLoading}
                              onClick={handleGetCaptcha}
                            >
                              {captchaData.captchaData ? (
                                <img
                                  className="captcha-image"
                                  src={captchaData.captchaData}
                                  alt={t('login.captcha', '验证码')}
                                />
                              ) : (
                                t('login.getCaptcha', '获取验证码')
                              )}
                            </Button>
                          </div>
                        </Form.Item>
                      )}

                      <Form.Item name="remember" valuePropName="checked">
                        <Checkbox>{t('login.remember', '记住登录状态')}</Checkbox>
                      </Form.Item>

                      <Form.Item>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={submitLoading}
                          block
                          className="login-submit-btn"
                        >
                          {t('login.login', '登录')}
                        </Button>
                      </Form.Item>

                      {enableQuickLogin && (
                        <Form.Item>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <Button
                              type="default"
                              onClick={() => handleQuickLogin('belle_admin')}
                              loading={quickLoginLoading}
                              style={{ flex: 1 }}
                              className="quick-login-btn"
                              title="tenant_id=10"
                            >
                              鞋业租户 (ID:10)
                            </Button>
                            <Button
                              type="default"
                              onClick={() => handleQuickLogin('lining_admin')}
                              loading={quickLoginLoading}
                              style={{ flex: 1 }}
                              className="quick-login-btn"
                              title="tenant_id=2"
                            >
                              运动品牌 (ID:2)
                            </Button>
                          </div>
                        </Form.Item>
                      )}
                    </Form>
                  </>
                ),
              },
              {
                key: 'qrcode',
                label: (
                  <span><QrcodeOutlined /> {t('login.qrcodeTab', '扫码登录')}</span>
                ),
                children: renderQRCodeContent(),
              },
            ]}
          />

          {/* REQ-20260508-0001 #10179: 平台超管隐藏入口（租户自己的域名下不显示） */}
          {!domainTenant && <div
            style={{
              marginTop: 16,
              paddingTop: 12,
              borderTop: '1px solid #f0f0f0',
              textAlign: 'center',
              fontSize: 12,
            }}
          >
            <a
              onClick={() => navigate('/platform/login')}
              style={{ color: '#94a3b8', cursor: 'pointer' }}
            >
              🛡️ 我是平台超管 ·{' '}
              <span style={{ color: '#6366f1' }}>前往平台管理登录入口 →</span>
            </a>
          </div>}

          {/* 页面底部语言切换 */}
          <div className="login-footer-actions">
            <div className="language-switcher">
              <GlobalOutlined className="language-icon" />
              {supportedLanguages.map((lang, index) => (
                <span key={lang.key}>
                  <span
                    className={`language-option ${i18n.language === lang.key ? 'active' : ''}`}
                    onClick={() => {
                      i18n.changeLanguage(lang.key);
                      localStorage.setItem('tenant-language', lang.key);
                    }}
                  >
                    {lang.flag} {lang.label}
                  </span>
                  {index < supportedLanguages.length - 1 && <span className="language-divider">|</span>}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 组织选择弹窗 */}
      <OrganizationSelectModal
        open={showOrgModal}
        onSuccess={handleOrgSelectSuccess}
        onClose={() => setShowOrgModal(false)}
        closable={false}
      />
    </div>
  );
};

export default Login;
